import {
  Alert,
  AlertIcon,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useCreatePreassignedInternalLoanTapesFromPreviewMetadataMutation } from 'packages/loanProduct/requests/requests.gql.generated';

type Props = {
  implementationId: string;
  previewIdentifier: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  receivableResponseTapePreviewJobId: string;
  isDisabled?: boolean;
  setInitiatingAssignmentConfirmationJob: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};
export const AssignmentSummaryConfirmButton = ({
  implementationId,
  previewIdentifier,
  reportingDate,
  fileVersion,
  receivableResponseTapePreviewJobId,
  isDisabled,
  setInitiatingAssignmentConfirmationJob,
}: Props) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [
    createPreassignedInternalLoanTapesFromPreviewMetadata,
    { loading, data },
  ] = useCreatePreassignedInternalLoanTapesFromPreviewMetadataMutation({
    onError: (err) => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
          ? 'Error while initiating assignment confirmation'
          : err.message;
        toast(errorToastConfig);
        onClose();
      }
    },
    variables: {
      implementationId,
      previewIdentifier,
      reportingDate,
      fileVersion,
      receivableResponseTapePreviewJobId,
    },
  });

  useEffect(() => {
    if (data) {
      setInitiatingAssignmentConfirmationJob(true);
      successToastConfig.title =
        'Initiated assignment confirmation. An email will be sent on completion.';
      toast(successToastConfig);
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const cancelRef = React.useRef(null);

  return (
    <>
      <Button
        colorScheme="aurora"
        aria-label="confirm assignment"
        isLoading={loading}
        onClick={onOpen}
        isDisabled={isDisabled}
      >
        Confirm Assignments
      </Button>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm assignments
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb="10px">
                Are you sure you would like to confirm assignment for the
                following?
              </Text>
              <Alert status="warning" mb="20px" mt="20px">
                <AlertIcon />
                <AlertDescription>
                  Receivable assignments cannot be altered after confirmation.
                </AlertDescription>
              </Alert>
              <Text>
                <strong>Reporting date: </strong>
                {commonDate.fullDateStrToHumanDateString(reportingDate)}
              </Text>
              <Text>
                <strong>File version: </strong>
                {fileVersion}
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef.current} onClick={onClose}>
                Cancel
              </Button>
              <Button
                isLoading={loading}
                colorScheme="aurora"
                onClick={async () => {
                  createPreassignedInternalLoanTapesFromPreviewMetadata();
                }}
                ml={3}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
